import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/auth'
import { useBrandingStore } from '../../store/branding'
import { useFeatureFlags } from '../../hooks/useFeatureFlag'
import { Badge } from '../ui/Badge'
import { CustomizerTrigger } from '../CustomizerPanel'

// Feature flag key for each nav item (undefined = always visible)
const NAV_FEATURE_FLAGS: Record<string, string | undefined> = {
  home: undefined,
  clients: undefined,
  sites: undefined,
  proposals: undefined,
  dispatch: undefined,
  tickets: 'tickets',
  kb: 'kb',
  network: undefined,
  products: undefined,
  'floor-plans': undefined,
  pipeline: 'crm_pipeline',
  invoices: undefined,
  'purchase-orders': undefined,
  deals: 'crm_pipeline',
  time: 'time_tracking',
  tasks: undefined,
  contracts: 'rmr_management',
  commissions: 'commission_tracking',
  assets: 'asset_tracking',
  cloud: undefined,
  vms: undefined,
  workflows: 'workflow_automation',
  settings: undefined,
  platform: undefined,
}

// Plan tier label for gated nav items
const NAV_PLAN_LABELS: Record<string, string> = {
  tickets: 'Pro',
  kb: 'Pro',
  assets: 'Pro',
  pipeline: 'Pro',
  deals: 'Pro',
  time: 'Pro',
  contracts: 'Pro',
  commissions: 'Pro',
  workflows: 'Enterprise',
}

// SVG icon paths (18px viewBox 24)
function SidebarIcon({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function HomeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}

function UsersIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

const ICON_PATHS = {
  mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z',
  fileText: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
  truck: 'M1 3h15v13H1z M16 8h4l3 3v5h-7V8z',
  ticket: 'M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z',
  receipt: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z',
  scrollText: 'M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 0 1 2-2h14',
  dollarSign: 'M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  shoppingCart: 'M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6',
  clock: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z',
  checkSquare: 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  bookOpen: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
  package: 'M16.5 9.4l-9-5.19 M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
  layers: 'M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5',
  map: 'M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  gauge: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z',
  panelLeftOpen: 'M3 3h18v18H3z M9 3v18',
  panelLeftClose: 'M3 3h18v18H3z M9 3v18 M15 9l-3 3 3 3',
  network: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z',
}

interface NavItemDef {
  id: string
  path: string
  label: string
  icon: React.FC<{ size?: number }>
  role?: string
}

const MAIN_NAV: NavItemDef[] = [
  { id: 'home', path: '/', label: 'Home', icon: HomeIcon },
  { id: 'clients', path: '/clients', label: 'Clients', icon: UsersIcon },
  { id: 'sites', path: '/sites', label: 'Sites', icon: (p) => <SidebarIcon d={ICON_PATHS.mapPin} {...p} /> },
  { id: 'proposals', path: '/proposals', label: 'Proposals', icon: (p) => <SidebarIcon d={ICON_PATHS.fileText} {...p} /> },
  { id: 'dispatch', path: '/dispatch', label: 'Dispatch', icon: (p) => <SidebarIcon d={ICON_PATHS.truck} {...p} /> },
]

const WORK_NAV: NavItemDef[] = [
  { id: 'tickets', path: '/tickets', label: 'Tickets', icon: (p) => <SidebarIcon d={ICON_PATHS.ticket} {...p} /> },
  { id: 'invoices', path: '/invoices', label: 'Invoices', icon: (p) => <SidebarIcon d={ICON_PATHS.receipt} {...p} /> },
  { id: 'contracts', path: '/contracts', label: 'Contracts', icon: (p) => <SidebarIcon d={ICON_PATHS.scrollText} {...p} /> },
  { id: 'commissions', path: '/commissions', label: 'Commissions', icon: (p) => <SidebarIcon d={ICON_PATHS.dollarSign} {...p} /> },
  { id: 'purchase-orders', path: '/purchase-orders', label: 'Purchase Orders', icon: (p) => <SidebarIcon d={ICON_PATHS.shoppingCart} {...p} /> },
  { id: 'time', path: '/time', label: 'Time Tracking', icon: (p) => <SidebarIcon d={ICON_PATHS.clock} {...p} /> },
  { id: 'tasks', path: '/tasks', label: 'Tasks', icon: (p) => <SidebarIcon d={ICON_PATHS.checkSquare} {...p} /> },
]

const TOOLS_NAV: NavItemDef[] = [
  { id: 'kb', path: '/kb', label: 'Knowledge Base', icon: (p) => <SidebarIcon d={ICON_PATHS.bookOpen} {...p} /> },
  { id: 'assets', path: '/assets', label: 'Assets', icon: (p) => <SidebarIcon d={ICON_PATHS.package} {...p} /> },
  { id: 'products', path: '/products', label: 'Products', icon: (p) => <SidebarIcon d={ICON_PATHS.layers} {...p} /> },
  { id: 'floor-plans', path: '/floor-plans', label: 'Floor Plans', icon: (p) => <SidebarIcon d={ICON_PATHS.map} {...p} /> },
  { id: 'cloud', path: '/cloud', label: 'Cloud', icon: (p) => <SidebarIcon d={ICON_PATHS.network} {...p} /> },
  { id: 'vms', path: '/vms', label: 'VMS Health', icon: (p) => <SidebarIcon d={ICON_PATHS.gauge} {...p} /> },
  { id: 'workflows', path: '/workflows', label: 'Workflows', icon: (p) => <SidebarIcon d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" {...p} /> },
]

const BOTTOM_NAV: NavItemDef[] = [
  { id: 'settings', path: '/settings', label: 'Settings', icon: (p) => <SidebarIcon d={ICON_PATHS.settings} {...p} /> },
  { id: 'platform', path: '/platform', label: 'Platform', icon: (p) => <SidebarIcon d={ICON_PATHS.gauge} {...p} />, role: 'super_admin' },
]

const SECTIONS_STORAGE_KEY = 'amber-sidebar-sections'

function loadSectionState(): { work: boolean; tools: boolean } {
  try {
    const stored = localStorage.getItem(SECTIONS_STORAGE_KEY)
    if (stored) return JSON.parse(stored)
  } catch { /* ignore */ }
  return { work: true, tools: true }
}

export function Sidebar({ onOpenCustomizer }: { onOpenCustomizer?: () => void }) {
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(() => localStorage.getItem('sidebar-pinned') === '1')
  const [sections, setSections] = useState(loadSectionState)
  const location = useLocation()
  const { user } = useAuthStore()
  const { companyNameShort } = useBrandingStore()
  const { flags } = useFeatureFlags()

  // FIX 3: Only auto-expand on hover when NOT pinned
  const expanded = pinned || hovered

  useEffect(() => {
    localStorage.setItem('sidebar-pinned', pinned ? '1' : '0')
  }, [pinned])

  useEffect(() => {
    localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(sections))
  }, [sections])

  const initials = user?.full_name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? 'U'

  const isFeatureEnabled = (itemId: string): boolean => {
    const flagKey = NAV_FEATURE_FLAGS[itemId]
    if (!flagKey) return true
    return flags[flagKey] ?? true
  }

  const isActive = (path: string, end?: boolean) => {
    if (end) return location.pathname === path
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const toggleSection = (key: 'work' | 'tools') => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  function renderNavItem(item: NavItemDef, end?: boolean) {
    const enabled = isFeatureEnabled(item.id)
    const active = isActive(item.path, end)
    const Icon = item.icon

    return (
      <NavLink
        key={item.id}
        to={item.path}
        end={end}
        className="sb-nav-item"
        title={!expanded ? item.label : undefined}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 14px',
          height: 34,
          minHeight: 34,
          flexShrink: 0,
          borderRadius: 8,
          background: active ? 'var(--accent-m)' : 'transparent',
          color: active ? 'var(--accent)' : 'var(--text-2)',
          textDecoration: 'none',
          fontSize: 13,
          fontWeight: active ? 500 : 400,
          fontFamily: 'var(--font)',
          transition: 'all var(--dur-f)',
          opacity: enabled ? 1 : 0.5,
        }}
      >
        {active && (
          <div style={{
            position: 'absolute', left: -6, top: '20%', bottom: '20%',
            width: 2, borderRadius: '0 2px 2px 0', background: 'var(--accent)',
          }} />
        )}
        <span style={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={18} />
        </span>
        {expanded && (
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', flex: 1 }}>{item.label}</span>
        )}
        {expanded && !enabled && NAV_PLAN_LABELS[item.id] && (
          <Badge v="accent" sm>{NAV_PLAN_LABELS[item.id]}</Badge>
        )}
      </NavLink>
    )
  }

  function renderSection(label: string, sectionKey: 'work' | 'tools', items: NavItemDef[]) {
    const open = sections[sectionKey]
    const filteredItems = items.filter(n => !n.role || user?.role === n.role)

    return (
      <div style={{ marginTop: expanded ? 16 : 8 }}>
        {expanded ? (
          <button
            onClick={() => toggleSection(sectionKey)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
              color: 'var(--text-3)', padding: '0 14px', marginBottom: 6,
              background: 'none', border: 'none',
              cursor: 'pointer', fontFamily: 'var(--font)', width: '100%',
              height: 20,
            }}
          >
            <span style={{
              fontSize: 8, transition: 'transform var(--dur-f)',
              transform: open ? 'rotate(90deg)' : 'none',
              display: 'inline-block',
            }}>&#9654;</span>
            {label}
          </button>
        ) : (
          <button
            onClick={() => toggleSection(sectionKey)}
            title={open ? `Collapse ${label}` : `Expand ${label}`}
            style={{
              width: '100%', height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <div style={{
              width: 16, height: 1, background: 'var(--border)',
              opacity: open ? 0.5 : 0.2,
            }} />
          </button>
        )}
        {open && (
          <div style={{
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {filteredItems.map(item => renderNavItem(item))}
          </div>
        )}
      </div>
    )
  }

  return (
    <aside
      className="v3-sidebar"
      style={{
        width: expanded ? 'var(--sidebar-e)' : 'var(--sidebar-c)',
        background: 'var(--bg-1)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        transition: `width var(--dur-n) var(--ease)`,
        overflow: 'hidden',
        flexShrink: 0,
        zIndex: 20,
        height: '100vh',
      }}
      onMouseEnter={() => { if (!pinned) setHovered(true) }}
      onMouseLeave={() => { if (!pinned) setHovered(false) }}
    >
      {/* Logo + Pin toggle (FIX 2: pin moved to header) */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 14px', minHeight: 56,
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: 'linear-gradient(135deg, var(--accent), var(--accent)88)',
            boxShadow: '0 0 16px var(--accent-g)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 700, color: 'var(--accent-t)',
          }}>
            A
          </div>
          {expanded && (
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>
              {companyNameShort || 'Amber'} Portal
            </span>
          )}
        </div>
        {expanded && (
          <button
            onClick={() => setPinned(v => !v)}
            title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: pinned ? 'var(--accent)' : 'var(--text-3)', padding: 4,
              borderRadius: 6, display: 'flex', alignItems: 'center',
              transition: 'color var(--dur-f)',
            }}
          >
            <SidebarIcon d={pinned ? ICON_PATHS.panelLeftClose : ICON_PATHS.panelLeftOpen} size={16} />
          </button>
        )}
      </div>

      {/* Main nav */}
      <nav style={{ padding: '8px 6px', display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
        {/* MAIN section */}
        {expanded && (
          <div style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
            color: 'var(--text-3)', padding: '6px 14px', marginBottom: 6,
          }}>Main</div>
        )}
        {MAIN_NAV.map(item => renderNavItem(item, item.id === 'home'))}

        {/* WORK section */}
        {renderSection('Work', 'work', WORK_NAV)}

        {/* TOOLS section */}
        {renderSection('Tools', 'tools', TOOLS_NAV)}
      </nav>

      {/* Bottom nav */}
      <nav style={{ padding: '4px 6px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
        {BOTTOM_NAV
          .filter(n => !n.role || user?.role === n.role)
          .map(item => renderNavItem(item))}
        {onOpenCustomizer && (
          <CustomizerTrigger expanded={expanded} onClick={onOpenCustomizer} />
        )}
      </nav>

      {/* User */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderTop: '1px solid var(--border)',
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 'var(--r-f)', flexShrink: 0,
          background: 'linear-gradient(135deg, var(--accent), var(--accent-h))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, color: 'var(--accent-t)', fontFamily: 'var(--mono)',
        }}>
          {initials}
        </div>
        {expanded && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>
              {user?.full_name ?? 'User'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
              {user?.role?.replace('_', ' ') ?? 'Viewer'}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .sb-nav-item:hover {
          background: rgba(255,255,255,.03) !important;
          color: var(--text-1) !important;
        }
        .sb-nav-item.active:hover {
          background: var(--accent-m) !important;
          color: var(--accent) !important;
        }
      `}</style>
    </aside>
  )
}
